import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { regels, sixpackKortingCent } from '@theme/smokkelaar-cart';

/**
 * De regel onder de prijs ("incl. btw · + € 0,15 statiegeld · € 10,58 per liter")
 * en de koopknop volgen het aantal (lijst C, productpagina):
 *  - statiegeld × aantal, met het aantal erbij: "+ € 0,90 statiegeld (6 blikjes)";
 *  - de literprijs blijft per liter;
 *  - vanaf twee stuks zegt de koopknop wat het kost: "Zet 6 in winkelwagen · € 19,92",
 *    en Horizons plakkende koopbalk toont hetzelfde bedrag.
 * Het aantal komt van Horizons stepper, ook als de keuzekaart "Sixpack" het zette.
 * Vanaf het sixpack-aantal rekent het bedrag met de sixpackkorting zoals de kassa:
 * per fles, afgekapt op de cent (sixpackKortingCent). Liquid geeft de getallen mee
 * (blocks/smokkelaar-statiegeld.liquid).
 *
 * Bij een variantwissel neemt dit element zijn eigen regel over uit de opnieuw
 * gerenderde sectie-HTML, net als Horizons product-price.js.
 */

const euro = (cent) => '€ ' + (cent / 100).toFixed(2).replace('.', ',');

class SmokkelaarPrijsregel extends HTMLElement {
  #sectie = null;
  #aantal = 1;

  connectedCallback() {
    this.addEventListener('click', (e) => {
      const knop = e.target.closest('[data-kies-aantal]');
      if (!knop || !this.#sectie) return;
      // De keuzekaart doet de rest (aantal in Horizons kiezer, aria-pressed).
      this.#sectie.querySelector(`smokkelaar-hoeveelheid [data-aantal="${CSS.escape(knop.dataset.kiesAantal)}"]`)?.click();
    });
    this.#sectie = this.closest('.shopify-section, dialog');
    this.#sectie?.addEventListener(StandardEvents.productSelect, this.#opVariantwissel);
    this.#sectie?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#opAantal);

    // Na "terug" kan de browser een eerder aantal in de stepper hebben teruggezet.
    const invoer = this.#sectie?.querySelector('product-form-component input[name="quantity"]');
    this.#aantal = Math.max(1, Number(invoer?.value) || 1);
    if (this.#aantal > 1) this.#toon();
  }

  disconnectedCallback() {
    this.#sectie?.removeEventListener(StandardEvents.productSelect, this.#opVariantwissel);
    this.#sectie?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#opAantal);
  }

  #opAantal = (event) => {
    // Alleen de stepper van het productformulier, niet die van de lade.
    if (event.detail?.cartLine || !(event.target instanceof Element) || !event.target.closest('product-form-component')) return;
    this.#aantal = Math.max(1, Number(event.detail.quantity) || 1);
    this.#toon();
  };

  #toon() {
    const n = this.#aantal;
    const prijs = Number(this.dataset.prijs);

    const regel = this.querySelector('[data-statiegeldregel]');
    if (regel) {
      const stuks = Number(this.dataset.stuks) * n;
      regel.textContent =
        `+ ${euro(Number(this.dataset.statiegeld) * n)} statiegeld` + (stuks > 1 ? ` (${stuks} ${this.dataset.eenheid})` : '');
    }

    let bedrag = prijs * n;
    if (this.dataset.sixpack === 'true' && n >= regels.sixpackAantal) {
      bedrag -= sixpackKortingCent(Array(n).fill(prijs));
    }
    this.#zetKnop(n > 1 ? `Zet ${n} in winkelwagen · ${euro(bedrag)}` : null);
    this.#zetBalk(n > 1 ? euro(bedrag) : null);
  }

  /**
   * Alleen de tekstknoop van de koopknop: icoon en Horizons vinkje-animatie blijven.
   * Horizons eigen tekst ("Zet in winkelwagen") gaat mee in data-smokkelaar-tekst;
   * morpht Horizon de knop bij een variantwissel, dan is die weg en begint het opnieuw.
   */
  #zetKnop(tekst) {
    const knop = this.#sectie?.querySelector('product-form-component [ref="addToCartButton"]');
    if (!knop || knop.disabled) return;
    const knoop = [...knop.childNodes].find((k) => k.nodeType === Node.TEXT_NODE && k.textContent.trim());
    if (!knoop) return;
    if (!('smokkelaarTekst' in knop.dataset)) knop.dataset.smokkelaarTekst = knoop.textContent;
    knoop.textContent = tekst ?? knop.dataset.smokkelaarTekst;
  }

  /**
   * De prijs in de plakkende koopbalk (sections/product-information.liquid): een
   * eigen span naast Horizons prijs, die zolang verborgen is. Bij een morph van de
   * balk verdwijnt de span vanzelf en staat Horizons prijs er weer.
   */
  #zetBalk(tekst) {
    const vak = this.#sectie?.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
    if (!vak) return;
    let eigen = vak.querySelector('[data-smokkelaar-balkprijs]');
    if (!eigen && !tekst) return;
    if (!eigen) {
      eigen = document.createElement('span');
      eigen.dataset.smokkelaarBalkprijs = '';
      vak.append(eigen);
    }
    eigen.textContent = tekst ?? '';
    eigen.hidden = !tekst;
    for (const kind of vak.children) if (kind !== eigen) kind.hidden = Boolean(tekst);
  }

  #opVariantwissel = (event) => {
    // Een variantwissel binnen een productkaart (quick add) gaat niet over deze pagina.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const nieuw = detail.html.querySelector(
          `smokkelaar-prijsregel[data-block-id="${this.dataset.blockId}"]`
        );
        if (!nieuw) return;
        this.innerHTML = nieuw.innerHTML;
        Object.assign(this.dataset, nieuw.dataset);
        // Pas na Horizons eigen verwerking van hetzelfde event: die morpht knop en
        // balk terug naar de nieuwe variant, daarna zetten wij het bedrag erop.
        setTimeout(() => this.#toon());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[smokkelaar-prijsregel]', error);
      });
  };
}

if (!customElements.get('smokkelaar-prijsregel')) {
  customElements.define('smokkelaar-prijsregel', SmokkelaarPrijsregel);
}
